import { Editor } from "@tinymce/tinymce-react";
import { debounce } from "lodash";
import { Loader2 } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import type { Bookmark, Editor as TransportTinyMCEEditor } from "tinymce";
import { tablePlaceholderTemplates } from "./tablePlaceholderTemplates";

// We define all available toolbars here, types are being automatically generated below.
const AVAILABLE_TOOLBARS = {
	only_comments: ["find"],
	empty: ["find"],
	mark_only: ["customHighlight"],
	assignment_mode: ["customHighlight"],
	minimal_complexity: ["undo redo | backcolor | bold underline | bullist"],
	basic: [
		"undo redo | cut copy paste | styles backcolor | bold italic underline strikethrough subscript superscript | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | charmap | table tabellenVorlagen | searchreplace preview",
	],
	author_mode: [
		"undo redo | bold underline | bullist numlist | table tabellenVorlagen",
	],
};

export const TAXPEER_PLUGINS = {
	comments: ["comments"], // the comment plugin will only work in the comment system
};

type ChoiceMenuItemSpec = {
	type: "choiceitem";
	text: string;
	value: string;
};

// Create a scoped version of the table border style
const createScopedTableBorderStyle = (editorId: string) => `
  #${editorId} table:not(.taxpeer) {
    border: 1px solid black !important;
    border-collapse: collapse !important;
  }
  #${editorId} table:not(.taxpeer) tbody td, #${editorId} table:not(.taxpeer) tbody th {
    border: 1px solid black !important;
  }
  #${editorId} table tbody td:empty::before, #${editorId} table tbody th:empty::before {
    content: '\\00a0';
    display: inline-block;
  }`;

const content_style = `
    .frac {
        display: inline-block; !important
        position: relative;
        vertical-align: middle;
        letter-spacing: 0.001em;
        text-align: center;
    }
    .frac > span {
        display: block;
        padding: 0.1em;
    }
    .frac span.bottom {
        border-top: thin solid black;
    }
    .frac span.symbol {
        display: none;
    }
    .tiny-heading {
        font-size: 1.15em;
        font-weight: bold;
        margin-top: 0.5em;
        margin-bottom: 0.5em;
    }
    /* Fix for table resizer bars */
    .ephox-snooker-resizer-bar {
        top: 0 !important;
        bottom: 0 !important;
        height: auto !important; /* Let height be determined by top/bottom */
		width: 5px !important;
        z-index: 10 !important;
    }
    .ephox-snooker-resizer-cols { /* Specific rule for column resizer */
       width: 5px !important; /* Force a reasonable width */
    }

	/* Inactive highlight for evaluation */
	.inactive-highlight {
		background-color: #e5e5e5 !important;
		opacity: 0.8;
	}

    /* Restore list styling that gets reset by Tailwind CSS */
    .taxpeer-editor ul {
        list-style-type: disc !important;
        margin: 1em 0 !important;
        padding-left: 40px !important;
    }
    .taxpeer-editor ol {
        list-style-type: decimal !important;
        margin: 1em 0 !important;
        padding-left: 40px !important;
    }
    .taxpeer-editor ul ul {
        list-style-type: circle !important;
        margin: 0.5em 0 !important;
    }
    .taxpeer-editor ul ul ul {
        list-style-type: square !important;
    }
    .taxpeer-editor ol ol {
        list-style-type: lower-alpha !important;
        margin: 0.5em 0 !important;
    }
    .taxpeer-editor ol ol ol {
        list-style-type: lower-roman !important;
    }
    .taxpeer-editor li {
        margin: 0.25em 0 !important;
        display: list-item !important;
    }

	.taxpeer-editor ul {
        list-style-type: disc !important;
        margin: 1em 0 !important;
        padding-left: 40px !important;
    }
`;

export type TaxpeerEditorProps = {
	// the available toolbars are defined in the AVAILABLE_TOOLBARS object
	toolbar?: keyof typeof AVAILABLE_TOOLBARS;
	enableComments?: boolean;
	value?: string;
	onChange?: (content: string) => void;
	placeholder?: string;
	readOnly?: boolean;
	id: string;
	showDefaultTableBorders?: boolean;
	plugins?: keyof typeof TAXPEER_PLUGINS;
	onFocus?: () => void;
	onBlur?: (content: string) => void;
	/**
	 * If true (default), place caret at the end of the content when the editor gains focus.
	 * Useful for inline editors where users expect to continue typing at the end.
	 */
	placeCursorAtEndOnFocus?: boolean;
	/**
	 * Optional: set external toolbar container width. Use numbers for px (e.g. 1400),
	 * strings for custom CSS widths like '100vw'. Defaults to '100vw'.
	 */
	toolbarContainerWidth?: number | string;
};

/**
 * This is a custom editor component that uses TinyMCE under the hood.
 * It is used to create a rich text editor with configurable toolbars and features.
 *
 * The component wraps TinyMCE's Editor component and provides simplilfied configuration
 * options through props. It supports different toolbar presets, commenting functionality,
 * and handles content changes through callbacks.
 *
 * @param props - The component props
 * @param props.toolbar - The toolbar preset to use (defined in AVAILABLE_TOOLBARS)
 * @param props.enableComments - Whether to enable commenting functionality
 * @param props.value - The initial content value for the editor
 * @param props.onChange - Debounced callback triggered when content changes
 * @param props.placeholder - The placeholder text for the editor
 * @param props.readOnly - Whether to disable the editor
 * @param props.id - The id of database entry
 * @param props.plugins - The plugins to use (defined in TAXPEER_PLUGINS)
 * @param props.showDefaultTableBorders - Whether to show default table borders, mostly used when USER creates tables
 * @returns A rich text editor component with the specified configuration
 */
export default memo(function TaxpeerEditor(props: TaxpeerEditorProps) {
	// Use a more specific type for the editor instance
	const editorRef = useRef<{ getContent: () => string } | null>(null);

	// Store a reference to the active editor for use in effects
	const activeEditorRef = useRef<TransportTinyMCEEditor | null>(null);

	// Add a flag to track if this is the first render
	const isInitializedRef = useRef(false);

	// Add loading state
	const [isEditorLoading, setIsEditorLoading] = useState(true);

	// Track if changes are coming from inside the editor
	const isInternalChangeRef = useRef(false);

	// Add a new ref to store cursor position
	const cursorPositionRef = useRef<{
		bookmark: Bookmark;
		scrollY: number;
	} | null>(null);
	// Add a new flag to track if we're in the middle of restoring cursor
	const isRestoringCursorRef = useRef(false);
	// Add a mutex to prevent overlapping update/restore cycles
	const updateInProgressRef = useRef(false);
	// Add a throttled update tracker
	const lastUpdateTimeRef = useRef(0);

	// Add a ref to track if onFocus has been called for the current focus session
	const hasFocusBeenCalledRef = useRef(false);

	// Keep latest props in refs for use inside a memoized debounced handler
	const latestOnChangeRef = useRef<TaxpeerEditorProps["onChange"]>(
		props.onChange,
	);
	const latestReadOnlyRef = useRef<boolean>(!!props.readOnly);

	useEffect(() => {
		latestOnChangeRef.current = props.onChange;
	}, [props.onChange]);

	useEffect(() => {
		latestReadOnlyRef.current = !!props.readOnly;
	}, [props.readOnly]);

	// Debounce the onChange handler to reduce update frequency
	// Create the debounced function once and read latest values from refs
	const debouncedOnChange = useMemo(
		() =>
			debounce((content: string) => {
				if (latestReadOnlyRef.current) {
					return;
				}

				// Prevent overlapping updates during fast typing
				if (updateInProgressRef.current) {
					return;
				}

				updateInProgressRef.current = true;

				// Rate limit updates during fast typing - skip if less than 100ms has passed
				const now = Date.now();
				if (now - lastUpdateTimeRef.current < 100) {
					updateInProgressRef.current = false;
					return;
				}
				lastUpdateTimeRef.current = now;

				// Mark this change as internal before triggering parent's onChange
				isInternalChangeRef.current = true;

				// Save cursor position before the update - use TinyMCE's most reliable bookmark type
				if (activeEditorRef.current?.selection) {
					try {
						cursorPositionRef.current = {
							bookmark: activeEditorRef.current.selection.getBookmark(2, true),
							scrollY: window.scrollY,
						};
					} catch (e) {
						console.error("Failed to save cursor position:", e);
					}
				}

				// Call the parent's debounced onChange handler if provided
				if (latestOnChangeRef.current) {
					latestOnChangeRef.current(content);
				}

				// Reset after a delay that matches or exceeds the debounce time
				setTimeout(() => {
					isInternalChangeRef.current = false;
					updateInProgressRef.current = false;
				}, 300);
			}, 300),
		[],
	);

	/**
	 * This will setup the plugins for the editor, when they are enabled in the props
	 * @param editor
	 */
	function setupPlugins(editor: TransportTinyMCEEditor) {
		if (props.plugins) {
			switch (props.plugins) {
				case "comments":
					// Minimal stub to avoid runtime/lint errors until comment plugin is implemented
					// Future: import and initialize the real commentSelectionPlugin here
					// eslint-disable-next-line @typescript-eslint/no-unused-expressions
					editor;
					break;
			}
		}
	}

	// Effect to handle external content updates
	useEffect(() => {
		// Skip if this is an internal change or the editor isn't initialized
		if (
			isInternalChangeRef.current ||
			!activeEditorRef.current ||
			isRestoringCursorRef.current
		) {
			return;
		}

		// Only update content if it differs from current editor content
		const editor = activeEditorRef.current;
		const currentContent = editor.getContent();

		if (props.value !== undefined && props.value !== currentContent) {
			// Store selection before content change
			const savedCursorPosition = cursorPositionRef.current;

			// Flag that we're in a content update cycle
			updateInProgressRef.current = true;

			// Update content
			editor.setContent(props.value);

			// Restore cursor position after content update
			if (savedCursorPosition?.bookmark) {
				// Mark that we're in the process of restoring cursor
				isRestoringCursorRef.current = true;

				// Use a short timeout to ensure the editor has processed the content update
				setTimeout(() => {
					try {
						// Add null check for selection
						if (activeEditorRef.current?.selection) {
							// Focus first to ensure selection operations work properly
							activeEditorRef.current.focus();

							// Attempt to restore the cursor position
							activeEditorRef.current.selection.moveToBookmark(
								savedCursorPosition.bookmark,
							);

							// If bookmark-based restoration failed, we need a fallback
							// Add null check for selection
							if (
								activeEditorRef.current?.selection &&
								activeEditorRef.current.getContent().length > 0
							) {
								// As a fallback, just put cursor at the end
								activeEditorRef.current.selection.select(
									activeEditorRef.current.getBody(),
									true,
								);
								activeEditorRef.current.selection.collapse(false);
							}

							// Restore scroll position
							window.scrollTo(window.scrollX, savedCursorPosition.scrollY);
						}
					} catch (e) {
						console.error("Failed to restore cursor position:", e);
					} finally {
						// Always clean up flags
						isRestoringCursorRef.current = false;
						updateInProgressRef.current = false;
					}
				}, 10); // Small delay to ensure DOM is ready
			} else {
				// Reset flags if we don't need to restore cursor
				updateInProgressRef.current = false;
			}
		}
	}, [props.value]);

	// Add cleanup effect
	useEffect(() => {
		return () => {
			// Cleanup when component unmounts
			if (activeEditorRef.current) {
				activeEditorRef.current.remove();
				activeEditorRef.current = null;
			}
		};
	}, []);

	// Ensure TinyMCE toolbar/header always stacks above editable text.
	// We move the toolbar into a dedicated external container using TinyMCE's
	// `fixed_toolbar_container` so it lives above the inline editor in normal
	// document flow. Additionally, we force the TinyMCE header to `position: static`
	// with a high z-index. Together this prevents the toolbar from behaving like a
	// fixed overlay that would cover the input. Internally, toolbar rows are 100% wide
	// and can wrap; externally, the container controls the actual width (px or vw via
	// `toolbarContainerWidth`) and can horizontally scroll, avoiding clipping.
	useEffect(() => {
		const existing = document.querySelector(
			'style[data-taxpeer-tinymce-zfix="true"]',
		) as HTMLStyleElement | null;
		if (existing) return;

		const style = document.createElement("style");
		style.setAttribute("data-taxpeer-tinymce-zfix", "true");
		style.innerHTML = `
			/* Increase stacking of TinyMCE inline toolbar/header over content */
			.tox .tox-editor-header { z-index: 9999 !important; position: static !important; }
			.tox .tox-toolbar, .tox .tox-toolbar__primary { z-index: 9999 !important; }
			.tox .tox-toolbar-overlord { z-index: 9999 !important; }
			/* Make toolbar/header fill available width when using external container */
			.tox .tox-editor-header,
			.tox .tox-toolbar,
			.tox .tox-toolbar__primary,
			.tox .tox-toolbar__overflow {
				width: 100% !important;
				max-width: none !important;
			}
			/* Allow wrapping if needed to avoid overflow */
			.tox .tox-toolbar,
			.tox .tox-toolbar__primary { flex-wrap: wrap !important; }
		`;
		document.head.appendChild(style);

		return () => {
			if (style.parentNode) style.parentNode.removeChild(style);
		};
	}, []);

	const placeholder = props.readOnly
		? ""
		: props.placeholder || "Hier tippen...";

	const dontShowTableHandlers = props.toolbar === "mark_only";

	const editorId = `taxpeer-editor-${props.id}`;
	const toolbarContainerId = `${editorId}-toolbar`;

	// Compute toolbar container width style (no fixed positioning dependency)
	const toolbarWidthStyle: React.CSSProperties | undefined = !props.readOnly
		? {
				width:
					typeof props.toolbarContainerWidth === "number"
						? `${props.toolbarContainerWidth}px`
						: props.toolbarContainerWidth || "100%",
				overflowX: "auto",
			}
		: undefined;

	return (
		<div className="w-full relative overflow-visible taxpeer-editor min-h-[50px]">
			{/* Dedicated toolbar container to avoid overlaying the editable content */}
			{!props.readOnly && (
				<div
					id={toolbarContainerId}
					className="mb-2 z-[9999]"
					style={toolbarWidthStyle}
				/>
			)}
			{isEditorLoading && (
				<div className="absolute inset-0 flex items-center justify-center bg-white/50 z-10">
					<Loader2 className="h-8 w-8 animate-spin text-gray-200" />
				</div>
			)}
			<Editor
				tinymceScriptSrc="/tinymce/tinymce.min.js"
				id={editorId}
				onInit={(_, editor: unknown) => {
					const typedEditor = editor as TransportTinyMCEEditor;
					editorRef.current = typedEditor;
					activeEditorRef.current = typedEditor;
					isInitializedRef.current = true;

					// Set loading to false when editor is ready
					setIsEditorLoading(false);

					// Initialize plugins after editor is ready
					if (typedEditor) {
						setupPlugins(typedEditor);

						// Add event listeners to track and maintain cursor position
						typedEditor.on("BeforeSetContent", (_) => {
							// Only save position if we're not already restoring
							if (!isRestoringCursorRef.current && typedEditor?.selection) {
								try {
									cursorPositionRef.current = {
										bookmark: typedEditor.selection.getBookmark(2, true),
										scrollY: window.scrollY,
									};
								} catch (e) {
									console.error(
										"Failed to save cursor in BeforeSetContent:",
										e,
									);
								}
							}
						});

						// Additional cursor stability during fast typing
						typedEditor.on("keydown", () => {
							// Don't allow updates during fast typing sequences
							lastUpdateTimeRef.current = Date.now();
						});

						// Set the initial background color state to yellow
					}
				}}
				// Only pass initialValue on first render, otherwise pass undefined to prevent reinitialization
				initialValue={isInitializedRef.current ? undefined : props.value || ""}
				onEditorChange={(content: string) => {
					// Only call the debounced handler if it exists (not in readOnly mode)
					if (debouncedOnChange) {
						debouncedOnChange(content);
					}
				}}
				init={{
					menubar: false,
					placeholder: placeholder,

					// Don't show toolbar in read-only mode (except for specific cases)
					toolbar: props.readOnly
						? false
						: AVAILABLE_TOOLBARS[props.toolbar || "basic"]
							? AVAILABLE_TOOLBARS[props.toolbar || "basic"]
							: ["basic"],
					toolbar_mode: "sliding",
					toolbar_location: "top",
					// Place toolbar into external container so it never covers the content
					fixed_toolbar_container: props.readOnly
						? undefined
						: `#${toolbarContainerId}`,

					// Set default backcolor to yellow
					color_default_background: "#ffff00",

					// Style formats, if table borders are enabled, add the scoped table border style
					content_style:
						content_style +
						(props.showDefaultTableBorders
							? createScopedTableBorderStyle(editorId)
							: ""),
					style_formats: [
						{ title: "Kopfzeile", inline: "span", classes: "tiny-heading" },
						{ title: "Absatz", format: "p" },
					],
					menu: {
						format: { title: "Format", items: "styleselect" },
					},

					// Table styles
					table_default_attributes: {
						border: 1,
					},
					table_default_style: {
						border: "1px solid black",
						border_collapse: "collapse",
					},

					// Disable resize bars in mark_only mode
					table_resize_bars: dontShowTableHandlers ? false : undefined,
					object_resizing: dontShowTableHandlers ? false : "table",
					plugins: dontShowTableHandlers
						? [
								"lists advlist charmap preview autoresize visualblocks visualchars",
							].join(" ")
						: [
								"table insertdatetime charmap searchreplace advlist charmap preview autoresize",
								// Add core plugins that might be required
								"autolink directionality visualblocks visualchars link table charmap pagebreak nonbreaking anchor insertdatetime lists wordcount help charmap emoticons",
							].join(" "),
					paste_as_dataimages: false,
					statusbar: false,
					visual: false,
					branding: false,
					elementpath: false,
					paste_data_images: false,
					height: 300,
					min_height: 200,
					resize: true,
					inline: true,
					language: "de",
					// Keep editable for mark_only and assignment modes to allow highlighting
					editable_root:
						!props.readOnly ||
						props.plugins === "comments" ||
						props.toolbar === "mark_only",
					toolbar_position: "top",
					autoresize_bottom_margin: 20,
					setup: (editor) => {
						setupPlugins(editor);
						activeEditorRef.current = editor;

						// Handle focus event - call onFocus only once per focus session
						editor.on("focus", () => {
							if (props.onFocus && !hasFocusBeenCalledRef.current) {
								hasFocusBeenCalledRef.current = true;
								props.onFocus();
							}

							// Optionally force caret to end on focus for better UX in inline editor
							if (props.placeCursorAtEndOnFocus !== false && !props.readOnly) {
								// Delay to allow native selection from click to settle, then move to end
								setTimeout(() => {
									try {
										const body = editor.getBody();
										if (!body) return;
										// Select entire body and collapse to end
										editor.selection?.select(body, true);
										editor.selection?.collapse(false);
									} catch (_) {
										// ignore
									}
								}, 0);
							}
						});

						// Handle blur event - reset the focus flag when editor loses focus and propagate content
						editor.on("blur", () => {
							hasFocusBeenCalledRef.current = false;
							if (props.onBlur) {
								try {
									props.onBlur(editor.getContent());
								} catch (_) {
									// ignore
								}
							}
						});

						// Handle Enter key to ensure proper scrolling with bottom margin
						// Maintains 70px space below cursor when pressing Enter
						editor.on("keydown", (e) => {
							if (e.key === "Enter") {
								// Use setTimeout to allow the new line to be created first
								setTimeout(() => {
									const selection = editor.selection;
									if (selection) {
										const range = selection.getRng();
										if (range) {
											// Get the current cursor element
											const cursorElement =
												range.startContainer.nodeType === 3
													? range.startContainer.parentElement
													: (range.startContainer as Element);

											if (cursorElement) {
												// Find the closest scrollable container
												const scrollContainer =
													cursorElement.closest(".overflow-y-auto");
												if (!scrollContainer) {
													// Fallback to window if no scrollable container found
													const rect = cursorElement.getBoundingClientRect();
													const windowHeight = window.innerHeight;
													const bottomMargin = 70;

													if (rect.bottom > windowHeight - bottomMargin) {
														window.scrollBy(
															0,
															rect.bottom - windowHeight + bottomMargin,
														);
													}
												} else {
													// Handle scrollable container
													const rect = cursorElement.getBoundingClientRect();
													const containerRect =
														scrollContainer.getBoundingClientRect();
													const bottomMargin = 70;

													// Check if cursor is near the bottom of the visible area
													if (
														rect.bottom >
														containerRect.bottom - bottomMargin
													) {
														const scrollAmount =
															rect.bottom - containerRect.bottom + bottomMargin;
														scrollContainer.scrollTop += scrollAmount;
													}
												}
											}
										}
									}
								}, 10); // Small delay to ensure DOM is updated
							}
						});

						editor.ui.registry.addButton("Bruch", {
							text: "Bruch",
							onAction: (_) => {
								editor.insertContent(
									`<span class="frac"> <span>1</span> <span class="symbol">/</span> <span class="bottom">2</span> </span>`,
								);
							},
						});

						// --- Custom Table Templates Logic ---
						const templateInsertions = {
							buchungssatz: "buchungssatz",
							bilanz: "bilanz",
							guv: "guv",
							staffel: "staffel",
							schulden: "schulden",
							aufteilung: "aufteilung",
							schuldenabzug: "schuldenabzug",
							zusammenfassung: "zusammenfassung",
						};

						// Add the table templates menu button only if templates are provided and not in mark_only/assignment mode
						if (
							tablePlaceholderTemplates.tableTemplates &&
							props.toolbar !== "mark_only"
						) {
							editor.ui.registry.addMenuButton("tabellenVorlagen", {
								icon: "table-classes",
								tooltip: "Tabellenvorlage einfügen",
								fetch: (callback) => {
									const tableTemplates =
										tablePlaceholderTemplates.tableTemplates || {};
									const menuItems = Object.entries(templateInsertions)
										.map(([key, templateName]) => {
											const template =
												tableTemplates[
													templateName as keyof typeof tableTemplates
												];
											if (!template) return null; // Skip if template definition is missing

											return {
												type: "menuitem" as const,
												text:
													template.title ||
													key.charAt(0).toUpperCase() + key.slice(1),
												onAction: () => {
													editor.insertContent(template.html);
												},
											};
										})
										.filter((item) => item !== null); // Remove null entries

									callback(menuItems);
								},
							});
						}

						// Handle placeholders in new rows only if placeholders are provided
						if (tablePlaceholderTemplates.rowTemplates) {
							editor.on("NewRow", (row: { node: HTMLTableRowElement }) => {
								const node = row.node;
								const parentTable = editor.dom.getParent(node, "table");
								if (!parentTable) return;

								const placeholderTemplates =
									tablePlaceholderTemplates.rowTemplates || {};

								// Find the matching template based on table class
								for (const [templateName, template] of Object.entries(
									placeholderTemplates,
								)) {
									if (parentTable.classList.contains(templateName)) {
										(template.cells || []).forEach((text, index) => {
											if (node.cells[index]) {
												// Use innerText to set plain text placeholders
												node.cells[index].innerText = text;
											}
										});
										break;
									}
								}
							});
						}
						// --- End Custom Table Templates Logic ---

						// Handle Tab behavior: insert space when not in list or table to prevent focus leaving editor
						if (props.toolbar !== "mark_only" && props.plugins !== "comments") {
							editor.on("keydown", (e) => {
								if (e.key === "Tab") {
									const currentNode = editor.selection?.getNode();
									const isInList = currentNode
										? editor.dom.getParent(currentNode, "li")
										: null;
									const isInTable = currentNode
										? editor.dom.getParent(currentNode, "td,th")
										: null;

									// Only override when we're not in list items or table cells
									if (!isInList && !isInTable) {
										e.preventDefault();
										e.stopPropagation();
										// Insert a single space instead of moving focus out of the editor
										editor.insertContent(" ");
										return false;
									}
								}
							});
						}

						// Handle mark_only mode
						if (props.toolbar === "mark_only") {
							let highlightColor = "#ffff00";
							let highlightMode = false;
							let styleElement: HTMLStyleElement | null = null;

							// Function to update button style
							const updateButtonStyle = () => {
								// Remove existing style if it exists
								if (styleElement?.parentNode) {
									styleElement.parentNode.removeChild(styleElement);
								}
								// Create and add new style
								styleElement = document.createElement("style");
								styleElement.setAttribute("data-highlight-style", "true");
								styleElement.innerHTML = `
									.tox-tbtn[aria-label="Text hervorheben"] .tox-icon svg rect,
									.tox-split-button[aria-label="Text hervorheben"] .tox-icon svg rect {
										fill: ${highlightColor};
										stroke: #000;
										stroke-width: 1px;
									}
									
									/* Visual indication of active state */
									.tox-split-button[aria-label="Text hervorheben"] {
										background-color: ${
											highlightMode ? "#e3e3e3" : "transparent"
										};
									}
									.tox-split-button[aria-label="Text hervorheben"]:hover {
										background-color: ${highlightMode ? "#d0d0d0" : "#e3e3e3"};
									}
								`;
								document.head.appendChild(styleElement);
							};

							// Make sure formatter is initialized before using it
							editor.on("init", () => {
								editor.formatter.register("highlight", {
									inline: "span",
									classes: "mark-highlight",
									styles: { backgroundColor: highlightColor },
								});
							});

							// Register custom highlight button (existing mark_only code)
							editor.ui.registry.addIcon(
								"custom-highlight",
								'<svg width="24" height="24"><rect x="4" y="4" width="16" height="16" fill="currentColor" stroke="#000" stroke-width="1"/></svg>',
							);

							editor.ui.registry.addSplitButton("customHighlight", {
								icon: "custom-highlight",
								tooltip: "Text hervorheben",
								presets: "color",
								columns: 5,
								select: (value) => {
									return value === highlightColor;
								},
								fetch: (callback) => {
									const items: ChoiceMenuItemSpec[] = [
										{ type: "choiceitem", text: "Gelb", value: "#ffff00" },
										{ type: "choiceitem", text: "Hellgrün", value: "#00ff00" },
										{ type: "choiceitem", text: "Türkis", value: "#00ffff" },
										{ type: "choiceitem", text: "Rosa", value: "#ff00ff" },
										{ type: "choiceitem", text: "Hellgelb", value: "#ffff99" },
										{
											type: "choiceitem",
											text: "Pastellgrün",
											value: "#ccffcc",
										},
										{
											type: "choiceitem",
											text: "Helles Blau",
											value: "#99ccff",
										},
										{
											type: "choiceitem",
											text: "Helles Lila",
											value: "#e6ccff",
										},
										{
											type: "choiceitem",
											text: "Helles Orange",
											value: "#ffcc99",
										},
										{ type: "choiceitem", text: "Beige", value: "#f5f5dc" },
									];
									callback(items);
								},
								onSetup: () => {
									// Initial button style setup
									updateButtonStyle();

									// Return cleanup function
									return () => {
										if (styleElement?.parentNode) {
											styleElement.parentNode.removeChild(styleElement);
										}
									};
								},
								onAction: () => {
									// Toggle highlight mode
									highlightMode = !highlightMode;
									// Update button style to reflect the new mode
									updateButtonStyle();
								},
								onItemAction: (_, value) => {
									highlightColor = value;
									if (editor.formatter) {
										editor.formatter.register("highlight", {
											inline: "span",
											classes: "mark-highlight",
											styles: { backgroundColor: highlightColor },
										});
									} else {
										editor.on("init", () => {
											editor.formatter.register("highlight", {
												inline: "span",
												classes: "mark-highlight",
												styles: { backgroundColor: highlightColor },
											});
										});
									}

									// Re-register formatter with new color
									editor.formatter.register("highlight", {
										inline: "span",
										classes: "mark-highlight",
										styles: { backgroundColor: highlightColor },
									});
									// Update button icon color immediately
									updateButtonStyle();

									// Apply the new color immediately if highlight mode is active and text is selected
									if (
										highlightMode &&
										editor.selection &&
										!editor.selection.isCollapsed()
									) {
										applyOrToggleHighlight();
									}
								},
							});

							// Add mouseup listener to apply highlight when mode is active
							editor.on("mouseup", (e) => {
								// Check if the click target is part of the table resizing UI
								const targetElement = e.target as HTMLElement;
								if (
									targetElement?.classList.contains("ephox-snooker-resizer-bar")
								) {
									return; // Don't apply highlight during table resize
								}

								// Only apply if highlight mode is on
								if (highlightMode) {
									applyOrToggleHighlight();
								}
							});

							function applyOrToggleHighlight() {
								// Use optional chaining and ensure formatter is checked
								if (
									editor?.selection &&
									!editor.selection?.isCollapsed() &&
									editor.formatter
								) {
									const isHighlighted = editor.formatter.match("highlight");

									if (isHighlighted) {
										editor.formatter.remove("highlight");
									} else {
										// Check for overlapping highlights (keep existing overlap check logic)
										// Ensure getRng is called only when selection is valid
										// Add null check for selection before calling getRng
										const range = editor.selection?.getRng();
										if (!range) return; // Exit if range couldn't be retrieved

										let overlapsWithHighlight = false;

										// Method 1: Check if the selection contains any highlighted parts
										const selectionContents = range.cloneContents();
										const highlightedInSelection =
											selectionContents.querySelectorAll(
												".assignment-highlight, .zugeordnet",
											);
										if (highlightedInSelection.length > 0) {
											overlapsWithHighlight = true;
										}

										// Method 2: Check if selection start or end points are inside highlighted spans
										if (!overlapsWithHighlight) {
											let node = range.startContainer;
											while (node && node !== editor.getBody()) {
												if (node.nodeType === 1) {
													// Element node
													const element = node as Element;
													if (
														element.classList &&
														(element.classList.contains(
															"assignment-highlight",
														) ||
															element.classList.contains("zugeordnet"))
													) {
														overlapsWithHighlight = true;
														break;
													}
												}
												node = node.parentNode as Node;
											}

											if (!overlapsWithHighlight) {
												node = range.endContainer;
												while (node && node !== editor.getBody()) {
													if (node.nodeType === 1) {
														// Element node
														const element = node as Element;
														if (
															element.classList &&
															(element.classList.contains(
																"assignment-highlight",
															) ||
																element.classList.contains("zugeordnet"))
														) {
															overlapsWithHighlight = true;
															break;
														}
													}
													node = node.parentNode as Node;
												}
											}
										}

										// Method 3: Check if any highlight is fully contained within the selection
										if (!overlapsWithHighlight) {
											editor.formatter.apply("highlight");
										} else {
											toast.error(
												"Verschachtelte Markierungen sind nicht erlaubt. Bitte wähle einen unmarkierten Bereich aus.",
											);
										}
									}

									// Use optional chaining for collapse
									editor.selection?.collapse(false);

									// Trigger content update
									if (debouncedOnChange && props.toolbar === "mark_only") {
										const currentContent = editor.getContent();
										debouncedOnChange(currentContent);
									}
								}
							}

							// Keep the keydown event handler for mark_only mode
							editor.on("keydown", (e) => {
								if (
									(e.ctrlKey || e.metaKey) &&
									(e.key === "c" || e.key === "a")
								) {
									return true;
								}
								e.preventDefault();
								e.stopPropagation();
								return false;
							});

							editor.on("input", (e) => {
								e.preventDefault();
								editor.undoManager.undo();
							});
						}
					},
					// Add these settings to help with troubleshooting
					extended_valid_elements: "*[*]", // Allow all elements
					verify_html: false,
					// Add these settings to help with cursor stability
					keep_styles: true,
					paste_block_drop: true,
				}}
			/>
		</div>
	);
});
